import type { MessengerLikeLikerPreview } from '@/api/client';
import { MessageLikeAvatar } from '@/components/Messenger/shared/messages/MessageLikeAvatar';
import './MessageLikeStrip.css';

export type MessageLikeStripProps = {
  count: number;
  likedByMe: boolean;
  variant: 'own' | 'peer';
  likersPreview?: MessengerLikeLikerPreview[] | null;
  interactive?: boolean;
  onToggle?: () => void;
};

const MAX_AVATARS = 3;

export function MessageLikeStrip({
  count,
  likedByMe,
  variant,
  likersPreview,
  interactive,
  onToggle,
}: MessageLikeStripProps) {
  if (count <= 0 && !likedByMe) return null;

  const likers = Array.isArray(likersPreview) ? likersPreview.slice(0, MAX_AVATARS) : [];
  const overflowN = count > MAX_AVATARS ? count - MAX_AVATARS : 0;
  const showAvatars = likers.length > 0;
  /** Без превью с сервера — только счётчик у реакции */
  const showCountFallback = !showAvatars && count > 0;

  /** В бабле — красное ❤️; свой лайк отличается только более тёмным фоном бейджа (класс ниже). */
  const rootClass = [
    'msgLikeStrip',
    `msgLikeStrip_${variant}`,
    likedByMe ? 'msgLikeStrip_liked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <span className="msgLikeStrip__emoji" aria-hidden>
        ❤️
      </span>
      {showCountFallback ? <span className="msgLikeStrip__count">{count}</span> : null}
      {showAvatars ? (
        <span className="msgLikeStrip__avatarStack" aria-hidden>
          {likers.map((liker, i) => (
            <MessageLikeAvatar key={liker.person_id} liker={liker} variant={variant} stackIndex={i} />
          ))}
          {overflowN > 0 ? (
            <span className="msgLikeStrip__overflow" title={`Ещё ${overflowN}`}>
              +{overflowN}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  if (interactive && onToggle) {
    return (
      <button
        type="button"
        className={rootClass}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label={likedByMe ? 'Снять лайк' : 'Поставить лайк'}
      >
        {inner}
      </button>
    );
  }

  return (
    <span className={rootClass} aria-label={`Лайков: ${count}`}>
      {inner}
    </span>
  );
}
